import axios from 'axios';
import { getConfigEndpoint } from '../endpoints/configEndpointManager';
import { getAdminToken } from '../auth/authManager';

/**
 * Sub-Role API Service
 * Handles all sub-role related API calls
 * Base endpoint: /admin-api/v1/sub-roles
 * Uses service prefix (getConfigEndpoint) not auth prefix (getAdminEndpoint)
 */

// Create axios instance for sub-role operations
const createSubRoleClient = () => {
  const serviceEndpoint = getConfigEndpoint();

  if (!serviceEndpoint || serviceEndpoint === '') {
    console.error('[SubRoleAPI] ❌ No config endpoint available! Cannot make API calls.');
    console.error('[SubRoleAPI] Empty endpoint value:', `"${serviceEndpoint}"`);
    console.error('[SubRoleAPI] This usually means you need to visit Public DNS Manager first.');
    throw new Error('Config service endpoint is not available. Please add endpoints in Public DNS Manager.');
  }

  // Ensure URL has protocol
  const formattedEndpoint = serviceEndpoint && !serviceEndpoint.startsWith('http')
    ? `https://${serviceEndpoint}`
    : serviceEndpoint;

  const baseURL = formattedEndpoint ? `${formattedEndpoint}/admin-api/v1` : '/admin-api/v1';
  const fullUrl = `${baseURL}/sub-roles`;
  

  return axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 10000,
  });
};

// Get auth headers with token
const getAuthHeaders = () => {
  const token = getAdminToken();
  return {
    Authorization: `Bearer ${token}`,
  };
};

const subRoleAPI = {
  /**
   * Get all sub-roles
   * @param {Object} params - Query parameters
   * @param {string} params.mainRole - Filter by main role
   * @param {boolean} params.isActive - Filter by active status
   * @returns {Promise} Response with sub-roles array
   */
  getAllSubRoles: async (params = {}) => {
    try {
      const client = createSubRoleClient();
      const queryString = new URLSearchParams(params).toString();
      const endpoint = queryString ? `/sub-roles?${queryString}` : '/sub-roles';
      const response = await client.get(endpoint, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get all sub-roles:', error);
      throw error;
    }
  },

  /**
   * Get specific sub-role by ID
   * @param {string} subRoleId - Sub-role ID
   * @returns {Promise} Response with sub-role details
   */
  getSubRoleById: async (subRoleId) => {
    try {
      const client = createSubRoleClient();
      const response = await client.get(`/sub-roles/${subRoleId}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to get sub-role ${subRoleId}:`, error);
      throw error;
    }
  },

  /**
   * Create new sub-role (Super Admin only)
   * @param {Object} data - Sub-role data
   * @param {string} data.name - Unique identifier
   * @param {string} data.displayName - Human-readable name
   * @param {string} data.description - Description
   * @param {string} data.mainRole - Main role (viewer, moderator, admin)
   * @param {Object} data.permissions - Permission object
   * @param {number} data.priority - Priority level
   * @returns {Promise} Response with created sub-role
   */
  createSubRole: async (data) => {
    try {
      const client = createSubRoleClient();
      const response = await client.post('/sub-roles', data, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create sub-role:', error);
      throw error;
    }
  },

  /**
   * Update sub-role (Super Admin only)
   * @param {string} subRoleId - Sub-role ID
   * @param {Object} data - Fields to update
   * @returns {Promise} Response with updated sub-role
   */
  updateSubRole: async (subRoleId, data) => {
    try {
      const client = createSubRoleClient();
      const response = await client.put(`/sub-roles/${subRoleId}`, data, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to update sub-role ${subRoleId}:`, error);
      throw error;
    }
  },

  /**
   * Delete sub-role (soft delete, Super Admin only)
   * @param {string} subRoleId - Sub-role ID
   * @returns {Promise} Response with deletion result
   */
  deleteSubRole: async (subRoleId) => {
    try {
      const client = createSubRoleClient();
      const response = await client.delete(`/sub-roles/${subRoleId}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to delete sub-role ${subRoleId}:`, error);
      throw error;
    }
  },

  /**
   * Get sub-roles by main role
   * @param {string} mainRole - Main role (viewer, moderator, admin)
   * @returns {Promise} Response with sub-roles for main role
   */
  getSubRolesByMainRole: async (mainRole) => {
    try {
      const client = createSubRoleClient();
      const url = `/sub-roles/main-role/${mainRole}`;
      const fullUrl = `${client.defaults.baseURL}${url}`;
      const response = await client.get(url, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      const client = createSubRoleClient();
      const url = `/sub-roles/main-role/${mainRole}`;
      const fullUrl = `${client.defaults.baseURL}${url}`;
      console.error(`[SubRoleAPI] Failed to get sub-roles for main role ${mainRole}:`);
      console.error(`[SubRoleAPI] Requested URL: ${fullUrl}`);
      if (error.response) {
        console.error(`[SubRoleAPI] Response status: ${error.response.status}`);
        console.error(`[SubRoleAPI] Response data:`, error.response.data);
      }
      throw error;
    }
  },

  /**
   * Assign sub-roles to an admin (Super Admin only)
   * @param {string} adminId - Admin ID
   * @param {Array<string>} subRoleIds - Array of sub-role IDs
   * @returns {Promise} Response with assignment result
   */
  assignSubRolesToAdmin: async (adminId, subRoleIds) => {
    try {
      const client = createSubRoleClient();
      const response = await client.post(`/sub-roles/assign/${adminId}`, { subRoleIds }, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to assign sub-roles to admin ${adminId}:`, error);
      throw error;
    }
  },

  /**
   * Get admin's assigned sub-roles
   * @param {string} adminId - Admin ID
   * @returns {Promise} Response with admin's sub-roles
   */
  getAdminSubRoles: async (adminId) => {
    try {
      const client = createSubRoleClient();
      const response = await client.get(`/sub-roles/admin/${adminId}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to get sub-roles for admin ${adminId}:`, error);
      throw error;
    }
  },

  /**
   * Get admin's combined permissions
   * @param {string} adminId - Admin ID
   * @returns {Promise} Response with merged permissions
   */
  getAdminPermissions: async (adminId) => {
    try {
      const client = createSubRoleClient();
      const response = await client.get(`/sub-roles/admin/${adminId}/permissions`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to get permissions for admin ${adminId}:`, error);
      throw error;
    }
  },

  /**
   * Get admin's accessible groups
   * @param {string} adminId - Admin ID
   * @returns {Promise} Response with accessible group IDs
   */
  getAdminAccessibleGroups: async (adminId) => {
    try {
      const client = createSubRoleClient();
      const response = await client.get(`/sub-roles/admin/${adminId}/accessible-groups`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to get accessible groups for admin ${adminId}:`, error);
      throw error;
    }
  },

  /**
   * Get admin's accessible instruments
   * @param {string} adminId - Admin ID
   * @returns {Promise} Response with accessible instruments and categories
   */
  getAdminAccessibleInstruments: async (adminId) => {
    try {
      const client = createSubRoleClient();
      const response = await client.get(`/sub-roles/admin/${adminId}/accessible-instruments`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to get accessible instruments for admin ${adminId}:`, error);
      throw error;
    }
  },

  /**
   * Get current admin's permissions
   * @returns {Promise} Response with current admin's merged permissions
   */
  getCurrentAdminPermissions: async () => {
    try {
      const client = createSubRoleClient();
      const response = await client.get('/sub-roles/current/permissions', {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get current admin permissions:', error);
      throw error;
    }
  },

  /**
   * Get current admin's sub-roles
   * @returns {Promise} Response with current admin's sub-roles
   */
  getCurrentAdminSubRoles: async () => {
    try {
      const client = createSubRoleClient();
      const response = await client.get('/sub-roles/current/sub-roles', {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get current admin sub-roles:', error);
      throw error;
    }
  },

  /**
   * Get current admin's accessible groups
   * @returns {Promise} Response with accessible group IDs
   */
  getCurrentAccessibleGroups: async () => {
    try {
      const client = createSubRoleClient();
      const response = await client.get('/sub-roles/accessible-groups', {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get current accessible groups:', error);
      throw error;
    }
  },

  /**
   * Get current admin's accessible instruments
   * @returns {Promise} Response with accessible instruments and categories
   */
  getCurrentAccessibleInstruments: async () => {
    try {
      const client = createSubRoleClient();
      const response = await client.get('/sub-roles/accessible-instruments', {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get current accessible instruments:', error);
      throw error;
    }
  },

  /**
   * Initialize default sub-roles (Super Admin only)
   * Creates default sub-roles for all main roles
   * @returns {Promise} Response with initialization result
   */
  initializeDefaultSubRoles: async () => {
    try {
      const client = createSubRoleClient();
      const response = await client.post('/sub-roles/initialize', {}, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to initialize default sub-roles:', error);
      throw error;
    }
  }
};

export default subRoleAPI;
